import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { DBFreelancer, FreelancerKind, FreelancerLink } from "@/lib/db-types";

/**
 * VIB-2925: mutasjoner for frilanserbanken. Audit skrives av DB-triggerne
 * (aktør = innlogget bruker via current_user_id()), så hookene logger ikke
 * selv. updated_by settes fra useAuth, aldri den impersonerte brukeren.
 */

export interface FreelancerInput {
  name: string;
  kind: FreelancerKind;
  company_name: string | null;
  organization_number: string | null;
  email: string | null;
  phone: string | null;
  location: string | null;
  day_rate_nok: number | null;
  hour_rate_nok: number | null;
  rate_note: string | null;
  links: FreelancerLink[];
  tags: string[];
  notes: string | null;
  /** Første er hovedfagfelt. */
  discipline_ids: string[];
}

const emptyToNull = (v: string | null) => (v?.trim() ? v.trim() : null);

function toRow(input: FreelancerInput) {
  if (!input.name.trim()) throw new Error("Navn mangler");
  if (input.discipline_ids.length === 0) throw new Error("Velg minst ett fagfelt");
  return {
    name: input.name.trim(),
    kind: input.kind,
    company_name: emptyToNull(input.company_name),
    organization_number: emptyToNull(input.organization_number)?.replace(/\s/g, "") ?? null,
    email: emptyToNull(input.email)?.toLowerCase() ?? null,
    phone: emptyToNull(input.phone),
    location: emptyToNull(input.location),
    day_rate_nok: input.day_rate_nok,
    hour_rate_nok: input.hour_rate_nok,
    rate_note: emptyToNull(input.rate_note),
    links: input.links,
    tags: input.tags,
    notes: emptyToNull(input.notes),
  };
}

/** Bytter fagfeltene ut i én omgang: sletter de som er borte, upserter resten med riktig hovedfagfelt. */
async function replaceDisciplines(freelancerId: string, disciplineIds: string[]) {
  const { data: existing, error } = await supabase
    .from("freelancer_discipline_links")
    .select("discipline_id")
    .eq("freelancer_id", freelancerId);
  if (error) throw error;
  const removed = (existing ?? []).map((l) => l.discipline_id as string).filter((id) => !disciplineIds.includes(id));
  if (removed.length > 0) {
    const { error: delErr } = await supabase
      .from("freelancer_discipline_links")
      .delete()
      .eq("freelancer_id", freelancerId)
      .in("discipline_id", removed);
    if (delErr) throw delErr;
  }
  // Fjern hovedmarkeringen først, så den unike indeksen ikke slår til underveis.
  const { error: clearErr } = await supabase
    .from("freelancer_discipline_links")
    .update({ is_primary: false })
    .eq("freelancer_id", freelancerId);
  if (clearErr) throw clearErr;
  const { error: upErr } = await supabase.from("freelancer_discipline_links").upsert(
    disciplineIds.map((discipline_id, i) => ({ freelancer_id: freelancerId, discipline_id, is_primary: i === 0 })),
    { onConflict: "freelancer_id,discipline_id" },
  );
  if (upErr) throw upErr;
}

export function useSaveFreelancer() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: FreelancerInput }): Promise<DBFreelancer> => {
      const row = toRow(input);
      const query = id
        ? supabase.from("freelancers").update({ ...row, updated_by: user?.id ?? null }).eq("id", id)
        : supabase.from("freelancers").insert({ ...row, created_by: user?.id ?? null });
      const { data, error } = await query.select().single();
      if (error) throw error;
      await replaceDisciplines(data.id, input.discipline_ids);
      return data as DBFreelancer;
    },
    onSuccess: async (row, vars) => {
      await invalidateFor(qc, "freelancers", { freelancerId: row.id });
      await invalidateFor(qc, "freelancer_discipline_links", { freelancerId: row.id });
      toast.success(vars.id ? "Frilanseren er lagret" : "Frilanseren er opprettet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke lagre frilanseren: ${err.message}`),
  });
}

export function useSetFreelancerArchived() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      const { error } = await supabase
        .from("freelancers")
        .update({
          archived_at: archived ? new Date().toISOString() : null,
          archived_by: archived ? user?.id ?? null : null,
          updated_by: user?.id ?? null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "freelancers", { freelancerId: vars.id });
      toast.success(vars.archived ? "Frilanseren er arkivert" : "Frilanseren er gjenopprettet");
    },
    onError: (err: Error) => toast.error(`Kunne ikke endre frilanseren: ${err.message}`),
  });
}

/** GDPR: sletter filene i Storage først (Storage-API-et, ikke SQL), så alt annet via erase_freelancer (admin). */
export function useEraseFreelancer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: files, error } = await supabase
        .from("freelancer_comment_attachments")
        .select("storage_path")
        .eq("freelancer_id", id)
        .not("storage_path", "is", null);
      if (error) throw error;
      const paths = (files ?? []).map((f) => f.storage_path as string);
      if (paths.length > 0) {
        const { error: rmErr } = await supabase.storage.from("freelancer-files").remove(paths);
        if (rmErr) throw rmErr;
      }
      const { error: rpcErr } = await supabase.rpc("erase_freelancer", { p_freelancer_id: id });
      if (rpcErr) throw rpcErr;
    },
    onSuccess: async (_d, id) => {
      await invalidateFor(qc, "freelancers", { freelancerId: id });
      toast.success("Frilanseren er slettet permanent");
    },
    onError: (err: Error) => toast.error(`Kunne ikke slette frilanseren: ${err.message}`),
  });
}
